import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../../utils/networking";
import dayjs from "dayjs";
import { BROADCAST_TYPES } from "./broadcastTypes";
import StatusChip from "./BroadcastStatusChip";
import BroadcastDetailDrawer from "./BroadcastDetailDrawer";

const columns: GridColDef[] = [
  {
    field: "createdAt",
    headerName: "Date",
    width: 160,
    renderCell: (params) =>
      params.value ? dayjs(params.value).format("MMM D, YYYY h:mm A") : "—",
  },
  {
    field: "broadcastType",
    headerName: "Type",
    width: 160,
    renderCell: (params) =>
      BROADCAST_TYPES.find((t) => t.id === params.value)?.label || params.value,
  },
  { field: "subject", headerName: "Subject", flex: 1, minWidth: 200 },
  { field: "fromBFC", headerName: "Sent By", width: 160 },
  {
    field: "deliveredCount",
    headerName: "Delivered",
    width: 100,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => (params.value || 0).toLocaleString(),
  },
  {
    field: "errorCount",
    headerName: "Errors",
    width: 90,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const count = params.value || 0;
      if (count === 0) return "0";
      return (
        <Chip
          label={count.toLocaleString()}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    },
  },
  {
    field: "recipientCount",
    headerName: "Total",
    width: 170,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      // `customRecipientCount` is absent on legacy broadcasts.
      const external = params.row?.customRecipientCount || 0;
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
            width: "100%",
          }}
        >
          <span>{params.value == null ? "—" : params.value.toLocaleString()}</span>
          {external > 0 && (
            <Chip
              label={`+${external.toLocaleString()} external`}
              size="small"
              variant="outlined"
              sx={{ borderColor: "#9d2362", color: "#9d2362" }}
            />
          )}
        </Box>
      );
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    renderCell: (params) => <StatusChip status={params.value} />,
  },
];

// Below 1200 px the columns share the grid's width instead of taking their
// fixed desktop widths; the grid scrolls sideways only once they are all at
// these minimums.
const FLEX_WIDTHS: Record<string, Pick<GridColDef, "flex" | "minWidth">> = {
  createdAt: { flex: 1.2, minWidth: 160 },
  broadcastType: { flex: 1, minWidth: 120 },
  subject: { flex: 1.6, minWidth: 140 },
  fromBFC: { flex: 1, minWidth: 130 },
  deliveredCount: { flex: 0.8, minWidth: 120 },
  errorCount: { flex: 0.6, minWidth: 110 },
  // Room for "1,234 +12 external".
  recipientCount: { flex: 0.8, minWidth: 170 },
  status: { flex: 0.8, minWidth: 110 },
};

const flexibleColumns: GridColDef[] = columns.map(({ width, ...column }) => ({
  ...column,
  ...FLEX_WIDTHS[column.field],
}));

const BroadcastHistory = () => {
  const theme = useTheme();
  const fixedColumnWidths = useMediaQuery(theme.breakpoints.up("lg"), {
    noSsr: true,
  });
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBroadcasts = async () => {
    try {
      const res = await axiosInstance.get("/broadcast");
      const broadcasts = (res.data.broadcasts || []).map((b: any) => ({
        ...b,
        id: b._id,
      }));
      setRows(broadcasts);
      return broadcasts;
    } catch (err) {
      console.error("Failed to fetch broadcasts:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroadcasts().then((broadcasts) => {
      const hasSending = broadcasts.some(
        (b: any) => b.status === "queued" || b.status === "sending",
      );
      if (hasSending) {
        pollRef.current = setInterval(async () => {
          const updated = await fetchBroadcasts();
          const stillSending = updated.some(
            (b: any) => b.status === "queued" || b.status === "sending",
          );
          if (!stillSending && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 5000);
      }
    });

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary">
          No broadcasts sent yet. Create your first broadcast in the "New Broadcast" tab.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 500 }}>
      <DataGrid
        rows={rows}
        columns={fixedColumnWidths ? columns : flexibleColumns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          setSelected(params.row);
          setDrawerOpen(true);
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#fce4ec" },
        }}
      />

      <BroadcastDetailDrawer
        key={selected?._id}
        open={drawerOpen}
        broadcast={selected}
        onClose={() => setDrawerOpen(false)}
        onExited={() => setSelected(null)}
      />
    </Box>
  );
};

export default BroadcastHistory;
